<template>
  <div class="max-w-2xl mx-auto space-y-8">
    <!-- Header -->
    <div class="text-center space-y-3">
      <div class="flex justify-center">
        <svg class="w-10 h-10 text-nirvana-lotus/40" viewBox="0 0 48 48" fill="currentColor">
          <path d="M24 4C24 4 16 12 16 20C16 24 19 27 24 28C29 27 32 24 32 20C32 12 24 4 24 4Z"/>
          <path d="M24 28C18 28 12 22 10 16C8 22 10 30 16 34C19 36 22 36 24 36C26 36 29 36 32 34C38 30 40 22 38 16C36 22 30 28 24 28Z" opacity="0.5"/>
        </svg>
      </div>
      <h1 class="text-3xl font-bold bg-gradient-to-r from-nirvana-cyan to-nirvana-lotus bg-clip-text text-transparent">
        มาอวยพรวันเกิดให้น้องเนอร์กัน~
      </h1>
    </div>

    <!-- Form Card -->
    <form @submit.prevent="handleSubmit" class="crystal-card space-y-6">
      <!-- Avatar Picker -->
      <div>
        <label class="block text-nirvana-silver/70 mb-3 text-sm font-medium">
          เลือกอวาตาร์ <span class="text-nirvana-lotus/60">*</span>
        </label>
        <div class="flex gap-3 flex-wrap">
          <button
            v-for="avatar in AVATARS"
            :key="avatar.id"
            type="button"
            @click="form.avatar = avatar.id"
            :class="[
              'flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all duration-200 min-w-[70px]',
              form.avatar === avatar.id
                ? 'border-nirvana-lotus/50 bg-nirvana-lotus/10 scale-105 shadow-glow-lotus'
                : 'border-white/5 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]'
            ]"
            :disabled="submitting"
          >
            <AvatarIcon :avatar-id="avatar.id" size="lg" />
            <span class="text-[10px] text-nirvana-silver/40">{{ avatar.label }}</span>
          </button>
        </div>
      </div>

      <div>
        <label class="block text-nirvana-silver/70 mb-2 text-sm font-medium" for="name">
          ชื่อ <span class="text-nirvana-lotus/60">*</span>
        </label>
        <input
          id="name"
          v-model.trim="form.name"
          type="text"
          required
          class="ethereal-input"
          placeholder="ชื่อของคุณ"
          :disabled="submitting"
        />
      </div>

      <div>
        <label class="block text-nirvana-silver/70 mb-2 text-sm font-medium" for="twitter">
          Twitter <span class="text-nirvana-silver/30">(optional)</span>
        </label>
        <div class="relative">
          <span class="absolute left-4 top-3 text-nirvana-cyan/30">@</span>
          <input
            id="twitter"
            v-model.trim="form.twitter"
            type="text"
            class="ethereal-input pl-9"
            placeholder="username"
            :disabled="submitting"
          />
        </div>
      </div>

      <div>
        <label class="block text-nirvana-silver/70 mb-2 text-sm font-medium" for="message">
          คำอวยพร <span class="text-nirvana-lotus/60">*</span>
        </label>
        <textarea
          id="message"
          v-model.trim="form.message"
          required
          rows="5"
          class="ethereal-input resize-none"
          placeholder="เขียนคำอวยพรให้น้องเนอร์..."
          :disabled="submitting"
        ></textarea>
      </div>

      <div>
        <label class="block text-nirvana-silver/70 mb-2 text-sm font-medium">
          รูปแฟนอาร์ต <span class="text-nirvana-silver/30">(optional)</span>
        </label>
        <ImageUpload
          v-model:file="form.fanart"
          :disabled="submitting"
          @click.stop
        />
      </div>

      <div
        v-if="error"
        class="bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg p-4 text-sm"
      >
        {{ error }}
      </div>

      <div class="ornate-divider"></div>

      <div class="flex justify-end items-center space-x-4">
        <router-link
          to="/"
          class="text-nirvana-silver/40 hover:text-nirvana-silver/70 transition-colors py-2 px-4 text-sm"
        >
          ยกเลิก
        </router-link>
        <button
          type="button"
          class="lotus-button opacity-50 cursor-not-allowed"
          disabled
        >
          🪷 ส่งคำอวยพร
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import ImageUpload from '@/components/ImageUpload.vue';
import AvatarIcon from '@/components/AvatarIcon.vue';
import { wishesAPI, uploadsAPI, submitWish } from '@/services/api';
import { AVATARS } from '@/config/avatars';

const router = useRouter();
const submitting = ref(false);
const error = ref(null);

const form = ref({
  name: '',
  twitter: '',
  message: '',
  fanart: null,
  avatar: 'lotus'
});

async function handleSubmit(e) {
  e.preventDefault();
  
  if (submitting.value) return;

  if (!form.value.name.trim() || !form.value.message.trim()) {
    error.value = 'Name and message are required.';
    return;
  }

  submitting.value = true;
  error.value = null;

  try {
    const formData = new FormData();
    formData.append('name', form.value.name.trim());
    formData.append('message', form.value.message.trim());
    formData.append('avatar', form.value.avatar);
    
    if (form.value.twitter.trim()) {
      formData.append('twitter', form.value.twitter.trim());
    }
    
    if (form.value.fanart) {
      formData.append('fanart', form.value.fanart);
    }

    await submitWish(formData);
    toast.success('Your wish has been submitted!');
    router.push('/');
  } catch (e) {
    error.value = e.message || 'Failed to submit wish';
    toast.error('Failed to submit wish');
  } finally {
    submitting.value = false;
  }
}
</script>
